/**
 * 后端业务错误码 → i18n key 通用映射（失败提示统一出口）
 *
 * 输入对齐 ApiResult 失败态（client.ts）：
 * - `code`：信封业务码（如 CONC_409 / VAL_400 / 3423；网络错/超时为 undefined）；
 * - `fallback`：`code: message` 拼接串（codeMsg）或传输异常 message。
 *
 * 返回 i18n key，组件内 `t(mapApiError(res.code, res.error))` 解析；未收录时原样返回
 * fallback（后端 message 原文不翻译，t() 对未命中 key 回显原文，见测试中的直通断言）。
 *
 * 收录口径：仅持卡人门户实际消费 API 可出现的码（ErrorCode.java 实读），
 * 禁止全量搬运 315 条码表。
 *
 * 纯函数（无 React / 无直接 i18next 依赖），可被 node 直跑单测。
 */

// 显式 .ts 扩展名：node --experimental-strip-types 直跑单测需要（tsconfig allowImportingTsExtensions）
import { isFactorMissing3410 } from './secondFactorError.ts';

/**
 * 码表 → i18n key。
 *
 * 34xx 家族与 secondFactorError / 既有引导路径打通（复用既有 key，不另起一套文案）：
 * - 3423 → security.ppNotSetGuide（未设支付密码，引导设置）；
 * - 3410 → 双语义（缺因子引导 / 验证码错误），由 isFactorMissing3410 按入参 message 区分；
 * - 3412/3424/3427/3411/RATE_LIMIT → 既有 security.* 文案；
 * - 3413/3414/PASSWORD_REQUIRED → 既有 twoFa.errors.* 文案。
 *
 * 刻意不收录（透传原文/由既有专用映射承接）：
 * - 3425：后端 message 为服务端拼装的固定风格文案（含锁定剩余分钟），原文即最佳展示
 *   （payment-password / mapSecondFactorError 两处既有口径一致）；
 * - PRODUCT_BIN_REQUIRED：由 lib/cardProductFetch 的 formatIssueCardError 专用处理。
 *
 * 422 家族已拆码（A5）：CARD_REVEAL_422（reveal 禁止）/ MFA_422（未绑 2FA）/
 * CARD_LIVENESS_422（强制活体）各自精确映射，禁止再用裸 "422" 判断。
 */
const API_ERROR_MESSAGE_KEYS: Readonly<Record<string, string>> = {
  // ---- 通用层 ----
  AUTH_401: 'errors.api.sessionExpired',
  AUTH_401_X: 'errors.api.sessionExpired',
  AUTH_401_T: 'errors.api.sessionExpired',
  AUTH_401_CH: 'errors.api.sessionExpired',
  AUTH_401_C: 'errors.api.badCredentials',
  AUTH_401_D: 'errors.api.accountDisabled',
  AUTH_403: 'errors.api.forbidden',
  VAL_400: 'errors.api.validation',
  '42901': 'errors.api.rateLimited',
  CONC_409: 'errors.api.conflict',
  '1008': 'errors.api.concurrentRequest',
  '1009': 'errors.api.idempotencyConflict',
  NOT_FOUND: 'errors.api.notFound',
  // ---- 卡与卡面域 ----
  CARD_404: 'errors.api.cardNotFound',
  CARD_409: 'errors.api.cardStatusForbidden',
  CARD_402: 'errors.api.cardInsufficient',
  CARD_407: 'errors.api.noShortfall',
  CARD_422: 'errors.api.cardSensitiveMissing',
  // 422 家族拆码后按精确码映射：reveal 禁止 / 未绑 2FA / 强制活体
  CARD_REVEAL_422: 'errors.api.cardStatusForbidden',
  MFA_422: 'security.mfaRequired',
  CARD_LIVENESS_422: 'cardDetail.activationLivenessHint',
  CARD_3302: 'errors.api.cardOpenLimit',
  CHN_502: 'errors.api.channelUnavailable',
  CHN_409: 'errors.api.channelRejected',
  // ---- 资金域（划转/充值/账户） ----
  ACC_402: 'errors.api.insufficientBalance',
  ACC_404: 'errors.api.accountNotFound',
  TRF_404: 'errors.api.transferNotFound',
  DEP_404: 'errors.api.depositNotFound',
  ADDR_404: 'errors.api.depositAddressMissing',
  RCFG_404: 'errors.api.depositConfigMissing',
  '3406': 'errors.api.creditInsufficient',
  '3407': 'errors.api.creditTargetInvalid',
  // ---- KYC / 资料 / 消息 / 文件 ----
  KYC_404: 'errors.api.kycNotFound',
  KYC_409: 'errors.api.kycStatusForbidden',
  KYC_403: 'errors.api.kycSelfOnly',
  '3420': 'errors.api.kycFineArrears',
  '3416': 'errors.api.passwordChangeRequired',
  '3417': 'errors.api.onboardingPending',
  MSG_404: 'errors.api.messageNotFound',
  FILE_404: 'errors.api.fileNotFound',
  '3415': 'errors.api.mfaUnavailable',
  PASSWORD_INCORRECT: 'errors.api.passwordIncorrect',
  // ---- 34xx 家族（复用既有引导文案，见文件头注） ----
  '3423': 'security.ppNotSetGuide',
  '3424': 'security.ppWrong',
  '3412': 'security.mfaRequired',
  '3427': 'security.secondFactorRequired',
  '3411': 'security.otpRateLimited',
  // 3432 代登录硬禁（§13.5）：文案与 merchant 门户同语义（apiErrorMessages.ts:3432）
  '3432': 'errors.api.impersonationForbidden',
  RATE_LIMIT: 'security.otpRateLimited',
  '3413': 'twoFa.errors.setupExpired',
  '3414': 'twoFa.errors.alreadyEnabled',
  PASSWORD_REQUIRED: 'twoFa.errors.passwordRequired',
};

/** 网络异常分类（code===undefined 且 fallback 未嵌已知码时）的 key */
const NETWORK_TIMEOUT_KEY = 'errors.network.timeout';
const NETWORK_SERVER_KEY = 'errors.network.server';
const NETWORK_CLIENT_KEY = 'errors.network.client';
const NETWORK_OFFLINE_KEY = 'common.networkError';

/**
 * 从 fallback 原文提取嵌入式业务码（`CODE: message` 或裸 `CODE` 形态）。
 * 仅当提取出的 token 命中码表（或 3410）才采用，防止把 'HTTP 502' /
 * 'network_error' / 'invalid_json' 等传输层字符串误判为业务码。
 */
function extractEmbeddedCode(fallback: string): string {
  const raw = (fallback || '').trim();
  const alpha = /^([A-Z][A-Z0-9_]{2,})(?::|$)/.exec(raw);
  if (alpha && (API_ERROR_MESSAGE_KEYS[alpha[1]] !== undefined || alpha[1] === '3410')) {
    return alpha[1];
  }
  const numeric = /^(\d{4})(?::|$)/.exec(raw);
  if (numeric && (API_ERROR_MESSAGE_KEYS[numeric[1]] !== undefined || numeric[1] === '3410')) {
    return numeric[1];
  }
  return '';
}

/** 网络异常分类：超时 / 服务端 5xx / 客户端 4xx / 连接失败（复用 common.networkError） */
function classifyNetworkError(fallback: string): string {
  const raw = (fallback || '').trim();
  if (/timeout|abort/i.test(raw)) {
    return NETWORK_TIMEOUT_KEY;
  }
  if (/^HTTP 5/.test(raw) || raw === 'invalid_json') {
    return NETWORK_SERVER_KEY;
  }
  if (/^HTTP 4/.test(raw)) {
    return NETWORK_CLIENT_KEY;
  }
  return NETWORK_OFFLINE_KEY;
}

/**
 * 业务错误码 → i18n key（未收录返回 fallback 原文，组件直接展示）。
 *
 * 判定顺序：
 * 1. code（或 fallback 前缀嵌入码）命中 3410 → 双语义分支（缺因子引导 / 验证码错误）；
 * 2. 命中码表 → 对应 key（34xx 家族复用既有引导文案）；
 * 3. 无码（网络错/超时/无信封 HTTP 错）→ 网络异常分类；
 * 4. 有码但未收录 → 透传 fallback 原文（后端 message 不翻译）。
 */
export function mapApiError(code: string | undefined, fallback: string): string {
  const normalized = (code || '').trim() || extractEmbeddedCode(fallback);
  if (normalized === '3410') {
    // 与 secondFactorError 打通：同码不同因（均未提供因子 → 二选一引导；否则 → 验证码不正确）
    return isFactorMissing3410(fallback) ? 'security.otpRequiredGuide' : 'security.otpInvalid';
  }
  const hit = API_ERROR_MESSAGE_KEYS[normalized];
  if (hit) {
    return hit;
  }
  if (!normalized) {
    return classifyNetworkError(fallback);
  }
  return fallback;
}

/**
 * 本模块引用的全部 i18n key（含 3410 双分支与网络分类），供双语存在性测试遍历。
 */
export const API_ERROR_I18N_KEYS: readonly string[] = Object.freeze([
  ...new Set([
    ...Object.values(API_ERROR_MESSAGE_KEYS),
    'security.otpRequiredGuide',
    'security.otpInvalid',
    NETWORK_TIMEOUT_KEY,
    NETWORK_SERVER_KEY,
    NETWORK_CLIENT_KEY,
    NETWORK_OFFLINE_KEY,
  ]),
]);

/** 仅供测试/守护脚本检视收录面（码表键；守护键 ⊆ ErrorCode.java 码值集合） */
export function apiErrorCodeKeys(): readonly string[] {
  return Object.freeze([...Object.keys(API_ERROR_MESSAGE_KEYS)]);
}
